import { useCallback, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useDocumentState } from '@/state'
import queryClient from '@/queryClient'
import { QUERY_KEYS as TEMPLATE_ATTRIBUTE_QUERY_KEYS } from '@/services/queries/template_attributes/queryKeys'

import { TemplateAttribute } from '@/lib/api/client'

export const useMultiSelectHandler = () => {
  const setDocumentState = useDocumentState(
    useShallow((state) => state.setState),
  )

  const getDocumentState = useDocumentState(
    useShallow((state) => state.getState),
  )

  const pressStartRef = useRef<{
    x: number
    y: number
    pointerId: number
  } | null>(null)

  const shouldIgnorePointerEvent = useCallback((eventTarget: EventTarget) => {
    if (!(eventTarget instanceof HTMLElement)) {
      return false
    }

    /**
     * Invariant: interactions inside portal-based popovers/dialogs should never
     * toggle document multi-select/drag behavior.
     */
    return Boolean(
      eventTarget.closest('[role="dialog"]') ||
        eventTarget.closest('[data-radix-popper-content-wrapper]'),
    )
  }, [])

  const onPointerDown = useCallback(
    (e: any, template: TemplateAttribute) => {
      if (
        document.querySelector('[role="dialog"]') ||
        shouldIgnorePointerEvent(e?.target)
      ) {
        return
      }

      // Track press start so we can distinguish click vs drag.
      if (typeof e?.clientX === 'number' && typeof e?.clientY === 'number') {
        pressStartRef.current = {
          x: e.clientX,
          y: e.clientY,
          pointerId: e.pointerId,
        }
      } else {
        pressStartRef.current = null
      }

      const isSelectKeyPressed = e.shiftKey || e.ctrlKey || e.metaKey
      if (!isSelectKeyPressed) {
        return
      }

      e.stopPropagation()

      const bulkSelectedTemplateAttributes =
        getDocumentState().bulkSelectedTemplateAttributes

      // Verify currently selected template is in the bulk-selected template attributes array
      const selectedElement = getDocumentState().selectedElement

      const containsSelectedTemplate = Boolean(
        selectedElement &&
          bulkSelectedTemplateAttributes.find(
            (t) => t.id === selectedElement?.id,
          ),
      )

      // If the selected template is not in the bulk-selected array then add it
      if (!containsSelectedTemplate) {
        const templateAttributes = queryClient
          .getQueriesData({
            queryKey: [TEMPLATE_ATTRIBUTE_QUERY_KEYS.TEMPLATE_ATTRIBUTES],
          })
          .flatMap((kv) => kv[1])
          .filter((t) => !!t) as any[]

        const selectedTemplate = templateAttributes.find(
          (t) => t.id === selectedElement?.id,
        )

        if (selectedTemplate) {
          bulkSelectedTemplateAttributes.push(selectedTemplate)
        }
      }

      // Toggle template
      const hasTemplate = Boolean(
        bulkSelectedTemplateAttributes.find((t) => t.id === template.id),
      )

      setDocumentState({
        bulkSelectedTemplateAttributes: hasTemplate
          ? bulkSelectedTemplateAttributes.filter((t) => t.id !== template.id)
          : [...bulkSelectedTemplateAttributes, template],
      })
    },
    [getDocumentState, setDocumentState, shouldIgnorePointerEvent],
  )

  const onPointerUp = useCallback(
    (e: any) => {
      if (
        document.querySelector('[role="dialog"]') ||
        shouldIgnorePointerEvent(e?.target)
      ) {
        return
      }

      const start = pressStartRef.current
      pressStartRef.current = null

      const isSelectKeyPressed = e.shiftKey || e.ctrlKey || e.metaKey
      if (isSelectKeyPressed) return

      if (!start || start.pointerId !== e.pointerId) return

      const dx = Math.abs(e.clientX - start.x)
      const dy = Math.abs(e.clientY - start.y)
      const CLICK_THRESHOLD_PX = 4

      const isClickLike = dx < CLICK_THRESHOLD_PX && dy < CLICK_THRESHOLD_PX
      if (!isClickLike) return

      setDocumentState({
        bulkSelectedTemplateAttributes: [],
      })
    },
    [setDocumentState, shouldIgnorePointerEvent],
  )

  return { onPointerDown, onPointerUp }
}
